import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PaletteDialog from './PaletteDialog';
import PaletteInput from './PaletteInput';
import PaletteList, { optionId } from './PaletteList';
import PaletteActions from './PaletteActions';
import PaletteFooter from './PaletteFooter';
import { intentFor } from './paletteKeys';
import { closePalette } from './paletteOpenState';
import { usePaletteRecent, usePaletteSearch, useRegistryVersion } from './usePaletteData';
import { buildPaletteCtx } from '../../palette/ctx';
import { parseQuery, detectPrefix } from '../../palette/parseQuery';
import { getProvider, getProviders, resolvePrefix } from '../../palette/registry';
import { buildRootRows, buildSearchRows, pinActive, moveActive, countResults } from '../../palette/rows';
import { recordVisit, forget } from '../../palette/frecency';
import { refOf } from '../../palette/types';
import { markPainted, endStart, markStart } from '../../palette/metrics';
import { suggestedCommands } from '../../palette/providers/commands';
import { listPages } from '../../palette/providers/pages';
import { tabResults } from '../../palette/providers/tabs';
import { useUiModalStore } from '../../store/uiModalStore';
import { usePreferencesStore } from '../../store/preferencesStore';
import { resolveShortcutKeys, shortcutLabelString } from '../../config/shortcuts';
import { isMacPlatform } from '../../utils/keyboard';
import './palette.css';

const LIST_ID = 'palette-listbox';
const MENU_ID = 'palette-actions';
const TASK_KEY_RE = /^[A-Za-z][A-Za-z0-9]{1,4}-[1-9][0-9]{0,8}$/;
const afterPaint = (fn) => (typeof requestAnimationFrame === 'function' ? requestAnimationFrame(() => fn()) : setTimeout(fn, 0));

function visibleActions(provider, result, ctx) {
  try {
    return (provider?.actions(result, ctx) || []).filter((a) => !a.when || a.when(ctx));
  } catch {
    return [];
  }
}

/**
 * Command palette v2 (PLAN §2, §3.1). State and effects only: the row model
 * is `palette/rows.js`, search is `palette/runSearch.js`, keys are
 * `paletteKeys.js`, the list is virtualised in `PaletteList`.
 *
 * `nav` = the ModalStack handlers (§3.5), handed to providers through ctx.
 */
export default function CommandPalette({ isOpen: isOpenProp, nav }) {
  const { t } = useTranslation();
  const storeOpen = useUiModalStore((s) => s.showCommandPalette);
  const isOpen = isOpenProp ?? storeOpen;
  const initialChip = useUiModalStore((s) => s.commandPaletteFilter);
  const mode = useUiModalStore((s) => s.commandPaletteMode);
  const keyboardShortcuts = usePreferencesStore((s) => s.keyboardShortcuts);
  const registryVersion = useRegistryVersion();
  const [input, setInput] = useState('');
  const [chip, setChip] = useState(null);
  const [chipUndo, setChipUndo] = useState(null);
  const [activeKey, setActiveKey] = useState(null);
  const [sub, setSub] = useState(null); // { item, actions, index }
  const [liveCount, setLiveCount] = useState('');
  const inputRef = useRef(null);
  const navRef = useRef(nav);
  navRef.current = nav;
  const paintPending = useRef(new Set());
  const firstPaintPending = useRef(false);

  const setScope = useCallback((id, text = '') => {
    setChip(id);
    setInput(text);
    setChipUndo(null);
    setSub(null);
    setActiveKey(null);
    inputRef.current?.focus();
  }, []);
  const controller = useMemo(() => ({ setScope, setQuery: (q) => setInput(q), close: () => closePalette() }), [setScope]);
  const getCtx = useCallback(() => buildPaletteCtx({ t, nav: navRef.current || {}, palette: controller }), [t, controller]);

  // Fresh state on every open; the chip comes from where it was opened (D2).
  useLayoutEffect(() => {
    if (!isOpen) return;
    setInput('');
    setChip(initialChip || null);
    setChipUndo(null);
    setSub(null);
    setActiveKey(null);
    inputRef.current?.focus({ preventScroll: true });
    markPainted('open', 'o', 'open:commit');
    afterPaint(() => markPainted('open', 'o', 'open'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);
  // Closed: drop the query too, so the next open renders the root view at
  // once instead of first re-running the last search (the dialog stays mounted).
  useLayoutEffect(() => {
    if (isOpen) return;
    endStart('open', 'o');
    setInput('');
    setChip(null);
    setChipUndo(null);
    setSub(null);
    setActiveKey(null);
  }, [isOpen]);

  const query = useMemo(() => (isOpen ? parseQuery(input, { chip }) : null), [isOpen, input, chip]);
  const onLanded = useCallback((landed) => paintPending.current.add(landed.providerId), []);
  const searchState = usePaletteSearch({ open: isOpen, query, getCtx, onLanded });
  const recent = usePaletteRecent({ open: isOpen, limit: mode === 'quickOpen' ? 20 : 8, getCtx });

  const labelOf = useCallback((id) => {
    const p = getProvider(id);
    return p ? t(p.labelKey) : id;
  }, [t]);

  const { items } = useMemo(() => {
    if (!isOpen) return { items: [] };
    const ctx = getCtx();
    const rowLabels = {
      recent: t('palette.group.recent'),
      suggested: t('palette.group.suggested'),
      pages: t('palette.group.pages'),
      tabs: t('palette.group.tabs'),
      topHit: t('palette.group.topHit'),
      showAll: (label) => t('palette.showAll', { label }),
      couldNotSearch: (label) => t('palette.error.couldNotSearch', { label }),
      noResults: (text) => (text ? t('palette.empty.noResults', { query: text }) : t('palette.empty.nothing')),
      searchMessages: (text) => t('palette.searchMessages', { query: text }),
      tryScopes: t('palette.empty.tryScopes'),
      offline: t('palette.offline'),
      partial: t('palette.messages.partial'),
      historyHidden: (days) => t('palette.messages.historyHidden', { days }),
    };
    if (!query || (!query.scope && !query.text)) {
      return {
        items: buildRootRows({
          recent,
          suggested: suggestedCommands(ctx),
          pages: listPages(ctx),
          tabs: mode === 'quickOpen' ? tabResults(ctx) : [],
          mode,
          labels: rowLabels,
        }),
      };
    }
    const groups = searchState?.groups || new Map();
    const order = getProviders().map((p) => p.id);
    const messages = getProvider('messages');
    let messagesAvailable = false;
    try {
      messagesAvailable = !!messages && messages.available(ctx);
    } catch {
      messagesAvailable = false;
    }
    return buildSearchRows({
      groups,
      order,
      scope: query.scope,
      text: query.text,
      labels: rowLabels,
      labelOf,
      messagesAvailable,
      offline: !ctx.online,
      exactKey: TASK_KEY_RE.test(query.text) ? query.text : null,
    });
    // registryVersion: a provider registering late re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, query, searchState, recent, mode, t, labelOf, registryVersion]);

  const active = pinActive(items, activeKey);
  const activeItem = items.find((i) => i.key === active) || null;

  // Paint marks for the §3.3 budgets (after the frame that shows them).
  useLayoutEffect(() => {
    if (!isOpen) return;
    const landed = [...paintPending.current];
    paintPending.current.clear();
    const first = firstPaintPending.current;
    firstPaintPending.current = false;
    if (!landed.length && !first) return;
    // Two marks: the DOM is committed (main-thread work done) and the next
    // frame (what the user sees, which also includes waiting for vsync).
    if (first) markPainted('query', 'q', 'first:commit');
    for (const id of landed) markPainted('query', 'q', `${id}:commit`);
    afterPaint(() => {
      if (first) markPainted('query', 'q', 'first');
      for (const id of landed) markPainted('query', 'q', id);
    });
  }, [items, isOpen]);

  // Live region: "12 results", debounced (§2.7).
  useEffect(() => {
    if (!isOpen) return undefined;
    const n = countResults(items);
    const id = setTimeout(() => setLiveCount(t('palette.liveCount', { count: n })), 500);
    return () => clearTimeout(id);
  }, [items, isOpen, t]);

  const close = useCallback((restoreFocus = true) => closePalette({ restoreFocus }), []);

  const runAction = useCallback((result, action, { newTab = false } = {}) => {
    const ctx = getCtx();
    if (!action.keepOpen) {
      recordVisit(refOf(result.provider, result.id));
      close(false);
    } else setSub(null);
    try {
      const out = action.run(result, ctx, { newTab });
      if (out && typeof out.catch === 'function') out.catch((e) => console.warn('palette action', action.id, e && e.name)); // eslint-disable-line no-console
    } catch (e) {
      console.warn('palette action', action.id, e && e.name); // eslint-disable-line no-console
    }
  }, [getCtx, close]);

  const runItem = useCallback((item, { newTab = false } = {}) => {
    if (!item) return;
    if (item.type === 'more') return setScope(item.providerId, query?.text || '');
    if (item.type === 'searchMessages') return setScope('messages', query?.text || '');
    if (item.type !== 'row') return;
    const r = item.result;
    const actions = visibleActions(getProvider(r.provider), r, getCtx());
    const action = (newTab && actions.find((a) => a.id === 'openNewTab')) || actions[0];
    if (action) runAction(r, action, { newTab });
  }, [getCtx, query, runAction, setScope]);

  const openSub = useCallback((item) => {
    if (!item || item.type !== 'row') return;
    const r = item.result;
    if (r.payload?.scopeProviderId) return setScope(r.payload.scopeProviderId);
    const actions = visibleActions(getProvider(r.provider), r, getCtx());
    if (actions.length) setSub({ item, actions, index: 0 });
  }, [getCtx, setScope]);

  const onChange = useCallback((value, at) => {
    markStart('query', 'q', at);
    firstPaintPending.current = true;
    if (!chip) {
      const ctx = getCtx();
      const hit = detectPrefix(value, (w) => {
        const p = resolvePrefix(w);
        try {
          return p && p.available(ctx) ? p : null;
        } catch {
          return null;
        }
      });
      if (hit) {
        setChip(hit.providerId);
        setInput(hit.rest);
        setChipUndo({ text: value, rest: hit.rest });
        setActiveKey(null);
        return;
      }
    }
    setInput(value);
    setChipUndo(null);
    setSub(null);
  }, [chip, getCtx]);

  const onKeyDown = useCallback((e) => {
    const el = e.currentTarget;
    const intent = intentFor(e, {
      caretAtEnd: el.selectionStart === el.value.length && el.selectionEnd === el.value.length,
      hasTextSelection: el.selectionStart !== el.selectionEnd,
      subMenuOpen: !!sub,
      isMac: isMacPlatform(),
    });
    if (intent !== 'backspace' && chipUndo) setChipUndo(null);
    if (!intent) return;
    const stop = () => {
      e.preventDefault();
      e.stopPropagation();
    };
    if (sub) {
      const n = sub.actions.length;
      if (intent === 'next' || intent === 'prev') {
        stop();
        setSub({ ...sub, index: (sub.index + (intent === 'next' ? 1 : -1) + n) % n });
      } else if (intent === 'home' || intent === 'end') {
        stop();
        setSub({ ...sub, index: intent === 'home' ? 0 : n - 1 });
      } else if (intent === 'run' || intent === 'runNewTab') {
        stop();
        runAction(sub.item.result, sub.actions[sub.index], { newTab: intent === 'runNewTab' });
      } else if (intent === 'back' || intent === 'escape') {
        stop();
        setSub(null);
      } else if (intent === 'actions' || intent === 'stay') stop();
      return;
    }
    if (intent === 'stay') {
      stop();
      return;
    }
    switch (intent) {
      case 'next':
      case 'prev':
        stop();
        setActiveKey(moveActive(items, active, intent === 'next' ? 1 : -1));
        break;
      case 'pageDown':
      case 'pageUp':
        stop();
        setActiveKey(moveActive(items, active, intent === 'pageDown' ? 10 : -10, { wrap: false }));
        break;
      case 'home':
      case 'end':
        stop();
        setActiveKey(moveActive(items, active, intent === 'home' ? -1e6 : 1e6, { wrap: false }));
        break;
      case 'run':
      case 'runNewTab':
        stop();
        runItem(activeItem, { newTab: intent === 'runNewTab' });
        break;
      case 'actions':
        if (activeItem?.type === 'row') {
          stop();
          openSub(activeItem);
        } else if (e.key === 'Tab') stop();
        break;
      case 'escape':
        stop();
        if (chip && !input) setChip(null);
        else close(true);
        break;
      case 'backspace':
        if (chipUndo && input === chipUndo.rest) {
          stop();
          setInput(chipUndo.text);
          setChip(null);
          setChipUndo(null);
        } else if (chip && !input) {
          stop();
          setChip(null);
        }
        break;
      case 'copyLink':
      case 'copyKey': {
        if (activeItem?.type !== 'row') break;
        const r = activeItem.result;
        const a = visibleActions(getProvider(r.provider), r, getCtx()).find((x) => x.id === (intent === 'copyLink' ? 'copyLink' : 'copyTaskKey'));
        if (a) {
          stop();
          runAction(r, a);
        }
        break;
      }
      default:
        break;
    }
  }, [sub, chipUndo, items, active, activeItem, chip, input, runAction, runItem, openSub, close, getCtx]);

  // Esc belongs to the palette while it is open: a WINDOW-capture listener
  // runs before every modal's document-capture Esc handler (Modal.js,
  // TaskDetailModal, EnvironmentsModal), so the modal under the palette stays
  // open (D4: "Esc closes only the palette"). Two-step: sub-menu / chip first.
  const escapeRef = useRef(null);
  escapeRef.current = () => {
    if (sub) setSub(null);
    else if (chip && !input) setChip(null);
    else close(true);
  };
  useEffect(() => {
    if (!isOpen) return undefined;
    const onWinKey = (e) => {
      if (e.key !== 'Escape' || e.isComposing) return;
      if (!e.target?.closest?.('[data-palette-root]') && document.activeElement !== inputRef.current) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      escapeRef.current();
    };
    window.addEventListener('keydown', onWinKey, true);
    return () => window.removeEventListener('keydown', onWinKey, true);
  }, [isOpen]);

  const onForget = useCallback((r) => forget(refOf(r.provider, r.id)), []);

  const chipProvider = chip ? getProvider(chip) : null;
  const chipInfo = chipProvider ? { label: t(chipProvider.labelKey), icon: chipProvider.icon } : null;
  const shortcut = shortcutLabelString(resolveShortcutKeys('commandPalette', keyboardShortcuts));
  const placeholder = chipInfo ? t('palette.placeholder.scoped', { label: chipInfo.label }) : mode === 'quickOpen' ? t('palette.placeholder.quickOpen') : t('palette.placeholder.root', { shortcut });
  const activeId = sub ? `${MENU_ID}-${sub.index}` : active ? optionId(LIST_ID, active) : null;

  return (
    <PaletteDialog open={isOpen} onDismiss={() => close(true)} labelledBy="palette-title">
      <div className={`palette-panel command-palette-panel${sub ? ' palette-panel--split' : ''}`} data-testid={isOpen ? 'command-palette' : undefined}>
        <h2 id="palette-title" className="palette-sr-only">{t('palette.title')}</h2>
        <PaletteInput
          ref={inputRef}
          value={input}
          onChange={onChange}
          onKeyDown={onKeyDown}
          chip={chipInfo}
          onRemoveChip={() => setScope(null, input)}
          placeholder={placeholder}
          listId={LIST_ID}
          activeId={activeId}
          expanded={items.length > 0}
          label={t('palette.inputLabel')}
          t={t}
        />
        <div className="palette-body">
          <div className="palette-main">
            <PaletteList
              listId={LIST_ID}
              items={items}
              activeKey={active}
              onHover={setActiveKey}
              onRun={(item) => runItem(item)}
              onContext={(item) => {
                setActiveKey(item.key);
                openSub(item);
              }}
              onForget={onForget}
              labelledBy="palette-title"
              t={t}
            />
          </div>
          {sub ? (
            <PaletteActions
              menuId={MENU_ID}
              title={sub.item.result.keyLabel ? `${sub.item.result.keyLabel} ${sub.item.result.title}` : sub.item.result.title}
              actions={sub.actions}
              activeIndex={sub.index}
              onHover={(i) => setSub((s) => (s ? { ...s, index: i } : s))}
              onRun={(a) => runAction(sub.item.result, a)}
              t={t}
            />
          ) : null}
        </div>
        <PaletteFooter t={t} sub={!!sub} chip={!!chip} activeItem={activeItem} />
        <div className="palette-sr-only" aria-live="polite" aria-atomic="true">{liveCount}</div>
      </div>
    </PaletteDialog>
  );
}
